import React, { useMemo, useState } from "react";
import { database } from "../firebaseConfig";
import { ref, update } from "firebase/database";
import StorageImage from "./StorageImage";
import { getStorage, ref as storageRef, getDownloadURL, uploadBytesResumable, deleteObject  } from "firebase/storage";
import { saveInfo, saveSection, saveArticle, createSection, createArticle } from "./services/firebase";


export default function AdminViewer({ data }) {
  // fallback per evitare errori se data non è ancora presente
  const safeData = data || { infoDelGiorno: "", sezioni: [] };
  //Quali sezioni sono "aperte" nella sidebar
  const [expandedSectionIds, setExpandedSectionIds] = useState(() => new Set());
  //L'elemento selezionato
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedArticleId, setSelectedArticleId] = useState(null);

  //Gestione dell'editing
  const [isEditing, setIsEditing] = useState(false);
  //Dice cosa stiamo modificando (info, section, article, new-section, new-article)
  const [editTarget, setEditTarget] = useState(null); // { type: 'info'|'section'|'article', sectionId?, articleId? }
  //Contiene la versione temporanea dei dati (form)
  const [draft, setDraft] = useState(null);
  //Feedback utente
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedTick, setSavedTick] = useState(0);
  //Gestione immagini sezione
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imgPreview, setImgPreview] = useState("");

  const [uploadPct, setUploadPct] = useState(0);
  //Gestione immagini articolo
  const [selectedArticleImageFile, setSelectedArticleImageFile] = useState(null);
  const [articleImgPreview, setArticleImgPreview] = useState("");
   

  

  // Helpers per trovare elementi
  //Funzioni per recuperare elementi dntro l'albero dei dati
  //Usate in salvataggio e selezione
  const getSectionById = (sectionId) =>
    safeData.sezioni?.find((s) => String(s.id) === String(sectionId)) || null;

  const getSectionIndexById = (sectionId) =>
    (safeData.sezioni || []).findIndex((s) => String(s.id) === String(sectionId));

  const getArticleById = (section, articleId) =>
    section?.articoli?.find((a) => String(a.id) === String(articleId)) || null;

  const getArticleIndexById = (section, articleId) =>
    (section?.articoli || []).findIndex((a) => String(a.id) === String(articleId));

  // Se immaginePath è "scienze-della-vita" => "immagini/sezioni/scienze-della-vita.jpg"
// Se contiene già "/" lo consideriamo un path completo.
// Se manca l'estensione, aggiungiamo ".jpg".
//Ricostruisce il percorso di un'immagine di sezione
//Gestisce casi in cui il path è solo slug, oppure completo, oppure senza estensione
function buildSectionImagePath(section) {
  const raw = (section?.immaginePath || "").trim();
  if (!raw) return "";
  let p = raw;
  if (!raw.includes("/")) p = `immagini/sezioni/${raw}`;
  if (!/\.(jpg|jpeg|png|webp|gif)$/i.test(p)) p += ".jpg";
  return p;
}
//Evita ricalcoli inutili.
//Tiene sempre aggiornati gli oggetti correnti selezionati
  const selectedSection = useMemo(
    () => (selectedSectionId != null ? getSectionById(selectedSectionId) : null),
    [selectedSectionId, safeData.sezioni]
  );

  const selectedArticle = useMemo(() => {
    if (!selectedSection || selectedArticleId == null) return null;
    return getArticleById(selectedSection, selectedArticleId);
  }, [selectedSection, selectedArticleId]);

  // Handlers 
  //controllano apertura/chiusura sidebar e cosa viene selezionato
  //Notare che ogni selezione resetta la modalità editing e gli upload
  const toggleSection = (sectionId) => {
    setExpandedSectionIds((prev) => {
      const next = new Set(prev);
      const key = String(sectionId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectSection = (sectionId) => {
    setSelectedSectionId(sectionId);
    setSelectedArticleId(null);
    setExpandedSectionIds((prev) => new Set(prev).add(String(sectionId)));
    exitEditMode();
    setSelectedImageFile(null);
    setImgPreview("");
    setUploadPct(0);

  };

  const handleSelectArticle = (sectionId, articleId) => {
    setSelectedSectionId(sectionId);
    setSelectedArticleId(articleId);
    setExpandedSectionIds((prev) => new Set(prev).add(String(sectionId)));
    setSelectedArticleImageFile(null);
    setArticleImgPreview("");
    exitEditMode();
  };

  // Editing helpers
  //Gestiscono un "draft" iniziale
  //Gestiscono passaggio da view => edit mode
  //exitEditMode resetta tutto
  const enterEditInfo = () => {
    setIsEditing(true);
    setEditTarget({ type: "info" });
    setDraft({ infoDelGiorno: safeData.infoDelGiorno || "" });
    setError("");
  };

  const enterEditSection = (section) => {
    setIsEditing(true);
    setEditTarget({ type: "section", sectionId: section.id });
    setDraft({
      titolo: section.titolo || "",
      slug: section.slug || "",
      descrizione: section.descrizione || "",
    });
    setError("");
  };

const enterEditArticle = (section, article) => {
  setIsEditing(true);
  setEditTarget({ type: "article", sectionId: section.id, articleId: article.id });
  setDraft({
    titolo: article.titolo || "",
    slug: article.slug || "",
    curiositaText: (article.curiosita || []).join("\n"),
  });
  setError("");
};

  const exitEditMode = () => {
    setIsEditing(false);
    setEditTarget(null);
    setDraft(null);
    setError("");
    //Reset dell'immagine selezionata
    setSelectedImageFile(null);
    setImgPreview("");
    setUploadPct(0);
  };

  const onDraftChange = (key, val) => setDraft((d) => ({ ...d, [key]: val }));


  //Salva:
  //  - infoDelGiorno
  //  - sezione (aggiornamento campi + upload nuova immagine)
  //  - articolo (item)
  //  - nuova sezione (creazione con immagine opzionale)
  //  - nuovo articolo
  //Ogni ramo del codice:
  //  - calcola l'indice giusto
  //  - fa "update()" su Firebase
  //  - gestisce upload e rimozione immagini su Storage
  //  - reset degli stati locali

  //Modificato. Questa funzione si occupa di orchestrare funzioni importate da "services/firebase.js"
  const handleSave = async () => {
  try {
    setSaving(true);
    setError("");

    if (editTarget.type === "info") {
      await saveInfo(draft);

    } else if (editTarget.type === "section") {
      await saveSection(selectedSection, draft, selectedImageFile, getSectionIndexById);

    } else if (editTarget.type === "article") {
      await saveArticle(selectedSection, selectedArticle, draft, selectedArticleImageFile, getSectionIndexById, getArticleIndexById);

    } else if (editTarget.type === "new-section") {
      await createSection(draft, selectedImageFile, safeData.sezioni);

    } else if (editTarget.type === "new-article") {
      await createArticle(selectedSection, draft, selectedArticleImageFile, getSectionIndexById);
    }

    setSavedTick(t => t + 1);
    exitEditMode();

  } catch (e) {
    setError(e.message || String(e));
  } finally {
    setSaving(false);
  }
};

  //FINE

  //Eliminano sezione o articolo dal DB
  //Prima rimuovono eventuali immagini dallo Storage
  //Poi aggiornano DB con array filtrato
const handleDeleteSection = async (sectionId) => {
  try {
    const sIdx = getSectionIndexById(sectionId);
    if (sIdx === -1) throw new Error("Sezione non trovata");

    const section = safeData.sezioni[sIdx];
    const storage = getStorage();

    // elimina immagine della sezione
    if (section.immaginePath) {
      try {
        await deleteObject(storageRef(storage, section.immaginePath));
        console.log("Immagine sezione eliminata:", section.immaginePath);
      } catch (err) {
        console.warn("Impossibile eliminare immagine sezione:", err);
      }
    }

    // elimina immagini degli articoli
    for (const articolo of section.articoli || []) {
      if (articolo.immaginePath) {
        try {
          await deleteObject(storageRef(storage, articolo.immaginePath));
          console.log("Immagine articolo eliminata:", articolo.immaginePath);
        } catch (err) {
          console.warn("Impossibile eliminare immagine articolo:", err);
        }
      }
    }

    // aggiorna DB senza questa sezione
    const updatedSezioni = safeData.sezioni.filter((s) => String(s.id) !== String(sectionId));
    await update(ref(database, "/"), { sezioni: updatedSezioni });

    // reset selezioni
    setSelectedSectionId(null);
    setSelectedArticleId(null);
  } catch (err) {
    setError(err.message || String(err));
  }
};

const handleDeleteArticle = async (sectionId, articleId) => {
  try {
    const sIdx = getSectionIndexById(sectionId);
    if (sIdx === -1) throw new Error("Sezione non trovata");

    const aIdx = getArticleIndexById(safeData.sezioni[sIdx], articleId);
    if (aIdx === -1) throw new Error("Articolo non trovato");

    const article = safeData.sezioni[sIdx].articoli[aIdx];
    const storage = getStorage();

    // elimina immagine articolo
    if (article.immaginePath) {
      try {
        await deleteObject(storageRef(storage, article.immaginePath));
        console.log("Immagine articolo eliminata:", article.immaginePath);
      } catch (err) {
        console.warn("Impossibile eliminare immagine articolo:", err);
      }
    }

    // aggiorna DB senza questo articolo
    const updatedArticoli = safeData.sezioni[sIdx].articoli.filter(
      (a) => String(a.id) !== String(articleId)
    );

    await update(ref(database, `/sezioni/${sIdx}`), { articoli: updatedArticoli });

    setSelectedArticleId(null);
  } catch (err) {
    setError(err.message || String(err));
  }
};



  // Layout 
  // Render principale
  return (
    <div style={styles.page}>
      <HeaderInfoDelGiorno
        infoDelGiorno={safeData.infoDelGiorno}
        isEditing={isEditing && editTarget?.type === "info"}
        draft={draft}
        onEdit={enterEditInfo}
        onDraftChange={onDraftChange}
        onCancel={exitEditMode}
        onSave={handleSave}
        saving={saving}
        savedTick={savedTick}
      />

      <div style={styles.body}>
        <aside style={styles.sidebar}>
          <DataExplorer
            sezioni={safeData.sezioni || []}
            expandedSectionIds={expandedSectionIds}
            onToggleSection={toggleSection}
            onSelectSection={handleSelectSection}
            onSelectArticle={handleSelectArticle}
            selectedSectionId={selectedSectionId}
            selectedArticleId={selectedArticleId}
            onAddSection={() => {
                setIsEditing(true);
                setEditTarget({ type: "new-section" });
                setDraft({ titolo: "", slug: "", descrizione: "" });
                setSelectedImageFile(null);
                setImgPreview("");
              }}
              onAddArticle={(sectionId) => {
                setIsEditing(true);
                setEditTarget({ type: "new-article", sectionId });
                setDraft({ titolo: "", slug: "", curiositaText: "" });
                setSelectedArticleImageFile(null);
                setArticleImgPreview("");
              }}

          />
        </aside>

        <main style={styles.main}>
          <DetailPanel
            section={selectedSection}
            article={selectedArticle}
            isEditing={isEditing}
            editTarget={editTarget}
            draft={draft}
            onEditSection={() => selectedSection && enterEditSection(selectedSection)}
            onEditArticle={() => selectedSection && selectedArticle && enterEditArticle(selectedSection, selectedArticle)}
            onDraftChange={onDraftChange}
            onCancel={exitEditMode}
            onSave={handleSave}
            saving={saving}
            error={error}
            savedTick={savedTick}
            setSelectedImageFile={setSelectedImageFile}
            imgPreview={imgPreview}
            setImgPreview={setImgPreview}
            selectedArticleImageFile={selectedArticleImageFile}
            setSelectedArticleImageFile={setSelectedArticleImageFile}
            articleImgPreview={articleImgPreview}
            setArticleImgPreview={setArticleImgPreview}
            onDeleteSection={handleDeleteSection}
            onDeleteArticle={handleDeleteArticle}  
            
            
          />
        </main>
      </div>
    </div>
  );
}

// Header ---------------------------------------------------------------
function HeaderInfoDelGiorno({ infoDelGiorno, isEditing, draft, onEdit, onDraftChange, onCancel, onSave, saving, savedTick }) {
  return (
    <header style={styles.header}>
      <div style={styles.headerInner}>
        <h1 style={styles.title}>Pannello Amministrazione — Dati</h1>
        <div style={styles.infoBox}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <div style={{ fontWeight: 600 }}>Info del giorno</div>
            {savedTick > 0 && <span style={styles.savedPill}>Salvato</span>}
          </div>
          {isEditing ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <textarea
                value={draft?.infoDelGiorno ?? ""}
                onChange={(e) => onDraftChange("infoDelGiorno", e.target.value)}
                rows={3}
                style={{ ...styles.textarea, flex: 1 }}
                placeholder="Scrivi l'info del giorno..."
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={onSave} disabled={saving} style={styles.primaryBtn}>Salva</button>
                <button onClick={onCancel} disabled={saving} style={styles.ghostBtn}>Annulla</button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {infoDelGiorno || "— Nessuna informazione presente —"}
              </p>
              <button onClick={onEdit} style={styles.secondaryBtn}>Modifica</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// Sidebar (Explorer) ---------------------------------------------------
function DataExplorer({
  sezioni,
  expandedSectionIds,
  selectedSectionId,
  selectedArticleId,
  onToggleSection,
  onSelectSection,
  onSelectArticle,
  onAddSection,       
  onAddArticle,       

}) {
  return (
    <div>
      <div style={styles.sidebarHeader}>Contenuti</div><div style={{ marginBottom: 12 }}>
          <button
            style={styles.primaryBtn}
            onClick={onAddSection}
          >
            + Aggiungi sezione
          </button>

      </div>

      <ul style={styles.sectionList}>
        {sezioni.map((sezione) => {
          const isExpanded = expandedSectionIds.has(String(sezione.id));
          const isSelectedSection = String(selectedSectionId) === String(sezione.id) && !selectedArticleId;
          return (
            <li key={sezione.id} style={styles.sectionItem}>
              <div
                style={{
                  ...styles.sectionRow,
                  ...(isSelectedSection ? styles.rowSelected : {}),
                }}
              >
                <button
                  onClick={() => onToggleSection(sezione.id)}
                  style={styles.chevronBtn}
                  title={isExpanded ? "Comprimi" : "Espandi"}
                >
                  <Chevron isOpen={isExpanded} />
                </button>

                <button
                  onClick={() => onSelectSection(sezione.id)}
                  style={styles.sectionBtn}
                  title={`Seleziona sezione: ${sezione.titolo}`}
                >
                  <div style={{ fontWeight: 600 }}>{sezione.titolo}</div>
                  <div style={styles.badge}>{sezione.articoli?.length || 0}</div>
                </button>
              </div>

              {isExpanded && (
                <ul style={styles.articleList}>
                  <li style={{ marginTop: 8 }}>
                   <button
                      style={styles.primaryBtn}
                      onClick={() => onAddArticle(sezione.id)}
                    >
                      + Aggiungi articolo
                    </button>

                  </li>

                  {(sezione.articoli || []).length === 0 && (
                    <li style={styles.articleEmpty}>Nessun articolo</li>
                  )}
                  {(sezione.articoli || []).map((articolo) => {
                    const isSelectedArticle =
                      String(selectedSectionId) === String(sezione.id) &&
                      String(selectedArticleId) === String(articolo.id);
                    return (
                      <li key={articolo.id}>
                        <button
                          onClick={() => onSelectArticle(sezione.id, articolo.id)}
                          style={{
                            ...styles.articleBtn,
                            ...(isSelectedArticle ? styles.rowSelected : {}),
                          }}
                          title={`Apri articolo: ${articolo.titolo}`}
                        >
                          {articolo.titolo}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Chevron({ isOpen }) {
  return (
    <span style={{ display: "inline-block", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 120ms ease" }}>
      ▶
    </span>
  );
}

// Detail Panel ---------------------------------------------------------
function DetailPanel({
  section,
  article,
  isEditing,
  editTarget,
  draft,
  onEditSection,
  onEditArticle,
  onDraftChange,
  onCancel,
  onSave,
  saving,
  error,
  savedTick,
  onDeleteSection,
  onDeleteArticle,
  // 👇 aggiunti per immagini articoli
  setSelectedImageFile,
  imgPreview,
  setImgPreview,
  setSelectedArticleImageFile,
  articleImgPreview,
  setArticleImgPreview,
}) {
      if (!section && !article && !(isEditing && (editTarget?.type === "new-section" || editTarget?.type === "new-article"))) {
          return (
            <div style={styles.emptyState}>
              Seleziona una sezione o un articolo per vedere i dettagli.
            </div>
          );
        }
  // -------------------------
  // NUOVA SEZIONE
  // -------------------------
  
  if (isEditing && editTarget?.type === "new-section") {
    return (
      <div>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>➕ Nuova Sezione</h2>
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Upload immagine */}
        <div style={{ marginTop: 16 }}>
          <div style={styles.fieldLabel}>Immagine sezione</div>

          {imgPreview && (
            <img
              src={imgPreview}
              alt="Anteprima nuova immagine"
              width={260}
              style={{ marginBottom: 8, borderRadius: 6 }}
            />
          )}

          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setSelectedImageFile(file);
                setImgPreview(URL.createObjectURL(file));
              }
            }}
          />
        </div>

        <LabeledInput
          label="Titolo"
          value={draft?.titolo ?? ""}
          onChange={(v) => onDraftChange("titolo", v)}
        />
        <LabeledInput
          label="Slug"
          value={draft?.slug ?? ""}
          onChange={(v) => onDraftChange("slug", v)}
        />
        <LabeledTextarea
          label="Descrizione"
          value={draft?.descrizione ?? ""}
          onChange={(v) => onDraftChange("descrizione", v)}
          rows={4}
        />

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={onSave} disabled={saving} style={styles.primaryBtn}>
            Salva
          </button>
          <button onClick={onCancel} disabled={saving} style={styles.ghostBtn}>
            Annulla
          </button>
        </div>
      </div>
    );
  }
  
   // -------------------------
  // FINE NUOVA SEZIONE
  // -------------------------


  
   // -------------------------
  // NUOVO ARTICOLO
  // -------------------------
  if (isEditing && editTarget?.type === "new-article") {
    return (
      <div>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>➕ Nuovo Articolo</h2>
        {error && <div style={styles.errorBox}>{error}</div>}

        {/* Upload immagine */}
        <div style={{ marginTop: 16 }}>
          <div style={styles.fieldLabel}>Immagine articolo</div>

          {articleImgPreview && (
            <img
              src={articleImgPreview}
              alt="Anteprima nuova immagine"
              width={260}
              style={{ marginBottom: 8, borderRadius: 6 }}
            />
          )}

          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                setSelectedArticleImageFile(file);
                setArticleImgPreview(URL.createObjectURL(file));
              }
            }}
          />
        </div>

        <LabeledInput
          label="Titolo"
          value={draft?.titolo ?? ""}
          onChange={(v) => onDraftChange("titolo", v)}
        />
        <LabeledInput
          label="Slug"
          value={draft?.slug ?? ""}
          onChange={(v) => onDraftChange("slug", v)}
        />
        <LabeledTextarea
          label="Curiosità (una per riga)"
          value={draft?.curiositaText ?? ""}
          onChange={(v) => onDraftChange("curiositaText", v)}
          rows={6}
        />

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={onSave} disabled={saving} style={styles.primaryBtn}>
            Salva
          </button>
          <button onClick={onCancel} disabled={saving} style={styles.ghostBtn}>
            Annulla
          </button>
        </div>
      </div>
    );
  }

  
  if (article) {
    const editingThis = isEditing && editTarget?.type === "article";

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <h2 style={{ marginTop: 0, marginBottom: 8 }}>Articolo</h2>
          {savedTick > 0 && <span style={styles.savedPill}>Salvato</span>}
        </div>
        {error && <div style={styles.errorBox}>{error}</div>}

        {editingThis ? (
          <>
            {/* Immagine articolo */}
            <div style={{ marginTop: 16 }}>
              <div style={styles.fieldLabel}>Immagine articolo</div>

              {articleImgPreview ? (
                <img
                  src={articleImgPreview}
                  alt="Anteprima nuova immagine"
                  width={260}
                  style={{ marginBottom: 8, borderRadius: 6 }}
                />
              ) : (
                article.immaginePath && (
                  <StorageImage
                    path={article.immaginePath}
                    alt={article.titolo}
                    width={260}
                    style={{ marginBottom: 8 }}
                  />
                )
              )}

              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setSelectedArticleImageFile(file);
                    setArticleImgPreview(URL.createObjectURL(file));
                  }
                }}
              />
            </div>

            <LabeledInput
              label="Titolo"
              value={draft?.titolo ?? ""}
              onChange={(v) => onDraftChange("titolo", v)}
            />
            <LabeledInput
              label="Slug"
              value={draft?.slug ?? ""}
              onChange={(v) => onDraftChange("slug", v)}
            />
            <LabeledTextarea
              label="Curiosità (una per riga)"
              value={draft?.curiositaText ?? ""}
              onChange={(v) => onDraftChange("curiositaText", v)}
              rows={6}
            />

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={onSave} disabled={saving} style={styles.primaryBtn}>
                Salva
              </button>
              <button onClick={onCancel} disabled={saving} style={styles.ghostBtn}>
                Annulla
              </button>
            </div>
          </>
        ) : (
          <>
            <ReadOnlyField label="Titolo" value={article.titolo} />
            <ReadOnlyField label="Slug" value={article.slug} />
            {article.contenuto && (
              <ReadOnlyField label="Contenuto" value={article.contenuto} multiline />
            )}

            {/* Immagine articolo (view mode) */}
            <div style={{ marginTop: 16 }}>
              <div style={styles.fieldLabel}>Immagine</div>
              {article.immaginePath ? (
                <StorageImage
                  path={article.immaginePath}
                  alt={article.titolo}
                  width={320}
                  style={{ marginTop: 6 }}
                />
              ) : (
                <div style={styles.fieldBox}>— Nessuna immagine —</div>
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={styles.fieldLabel}>Curiosità</div>
              {article.curiosita && article.curiosita.length > 0 ? (
                <ol style={{ marginTop: 6 }}>
                  {article.curiosita.map((c, idx) => (
                    <li key={idx} style={{ marginBottom: 6 }}>
                      {c}
                    </li>
                  ))}
                </ol>
              ) : (
                <div style={styles.fieldBox}>— Nessuna curiosità —</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={onEditArticle} style={styles.secondaryBtn}>
                Modifica
              </button>
              <button
                  onClick={() => window.confirm("Eliminare questo articolo?")
                    && onDeleteArticle(section.id, article.id)}
                  style={styles.ghostBtn}
                >
                  Elimina
              </button>
            </div>
          </>
        )}
      </div>
    );
  }
  //onEditSection
  // -------------------------
  // SEZIONE (già funzionante)
  // -------------------------




  const editingThis = isEditing && editTarget?.type === "section";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>Sezione</h2>
        {savedTick > 0 && <span style={styles.savedPill}>Salvato</span>}
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}

      {editingThis ? (
        <>
          <div style={{ marginTop: 16 }}>
            <div style={styles.fieldLabel}>Immagine sezione</div>

            {imgPreview ? (
              <img
                src={imgPreview}
                alt="Anteprima nuova immagine"
                width={260}
                style={{ marginBottom: 8, borderRadius: 6 }}
              />
            ) : (
              section.immaginePath && (
                <StorageImage
                  path={section.immaginePath}
                  alt={section.titolo}
                  width={260}
                  style={{ marginBottom: 8 }}
                />
              )
            )}

            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedImageFile(file);
                  setImgPreview(URL.createObjectURL(file));
                }
              }}
            />
          </div>

          <LabeledInput
            label="Titolo"
            value={draft?.titolo ?? ""}
            onChange={(v) => onDraftChange("titolo", v)}
          />
          <LabeledInput
            label="Slug"
            value={draft?.slug ?? ""}
            onChange={(v) => onDraftChange("slug", v)}
          />
          <LabeledTextarea
            label="Descrizione"
            value={draft?.descrizione ?? ""}
            onChange={(v) => onDraftChange("descrizione", v)}
            rows={4}
          />

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={onSave} disabled={saving} style={styles.primaryBtn}>
              Salva
            </button>
            <button onClick={onCancel} disabled={saving} style={styles.ghostBtn}>
              Annulla
            </button>
          </div>
        </>
      ) : (
        <>
          <ReadOnlyField label="Titolo" value={section.titolo} />
          <ReadOnlyField label="Slug" value={section.slug} />
          <ReadOnlyField label="Descrizione" value={section.descrizione} multiline />
          <ReadOnlyField label="N. articoli" value={section.articoli?.length ?? 0} />

          <div style={{ marginTop: 16 }}>
            <div style={styles.fieldLabel}>Immagine</div>
            {section.immaginePath ? (
              <StorageImage
                path={section.immaginePath}
                alt={section.titolo}
                width={320}
                style={{ marginTop: 6 }}
              />
            ) : (
              <div style={styles.fieldBox}>— Nessuna immagine —</div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={onEditSection} style={styles.secondaryBtn}>
              Modifica
            </button>
            <button
                  onClick={() => window.confirm("Eliminare questa sezione e tutti i suoi articoli?")
                    && onDeleteSection(section.id)}
                  style={styles.ghostBtn}
                >
                  Elimina
            </button>
          </div>
        </>
      )}
    </div>
  );
}


function ReadOnlyField({ label, value, multiline }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={styles.fieldLabel}>{label}</div>
      <div
        style={{
          ...styles.fieldBox,
          ...(multiline ? { whiteSpace: "pre-wrap" } : {}),
        }}
      >
        {value !== undefined && value !== null && String(value).length > 0
          ? String(value)
          : "—"}
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={styles.fieldLabel}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={styles.input}
      />
    </div>
  );
}

function LabeledTextarea({ label, value, onChange, rows = 4 }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={styles.fieldLabel}>{label}</div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        style={styles.textarea}
      />
    </div>
  );
}

// Styles ---------------------------------------------------------------
const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    background: "#f6f7f9",
    color: "#1f2937",
    fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, Noto Sans, \"Apple Color Emoji\", \"Segoe UI Emoji\"",
  },
  header: {
    padding: "16px 24px",
    background: "#fff",
    borderBottom: "1px solid #e5e7eb",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  headerInner: {
    maxWidth: 1200,
    margin: "0 auto",
  },
  title: {
    fontSize: 20,
    margin: 0,
    marginBottom: 12,
  },
  infoBox: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: 12,
  },
  savedPill: {
    fontSize: 12,
    background: "#ecfdf5",
    border: "1px solid #10b981",
    color: "#065f46",
    borderRadius: 999,
    padding: "2px 8px",
  },
  body: {
    display: "grid",
    gridTemplateColumns: "320px 1fr",
    gap: 16,
    maxWidth: 1200,
    width: "100%",
    padding: 16,
    margin: "0 auto",
    flex: 1,
  },
  sidebar: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: 12,
    overflow: "auto",
  },
  main: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: 16,
    overflow: "auto",
  },
  sidebarHeader: {
    fontWeight: 700,
    marginBottom: 8,
  },
  sectionList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  sectionItem: {
    marginBottom: 4,
  },
  sectionRow: {
    display: "grid",
    gridTemplateColumns: "28px 1fr",
    gap: 6,
    alignItems: "center",
    padding: 6,
    borderRadius: 6,
  },
  rowSelected: {
    background: "#eef2ff",
    outline: "1px solid #c7d2fe",
  },
  chevronBtn: {
    cursor: "pointer",
    background: "transparent",
    border: "none",
    padding: 2,
    fontSize: 14,
    lineHeight: 1,
  },
  sectionBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    textAlign: "left",
    padding: 0,
  },
  badge: {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 999,
    fontSize: 12,
    padding: "2px 8px",
  },
  articleList: {
    listStyle: "none",
    margin: 0,
    padding: "0 0 6px 34px",
  },
  articleBtn: {
    cursor: "pointer",
    background: "transparent",
    border: "none",
    width: "100%",
    textAlign: "left",
    padding: "6px 8px",
    borderRadius: 6,
  },
  articleEmpty: {
    color: "#6b7280",
    fontStyle: "italic",
    paddingLeft: 34,
    paddingTop: 4,
    paddingBottom: 8,
  },
  emptyState: {
    color: "#6b7280",
    fontStyle: "italic",
  },
  fieldLabel: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
  },
  fieldBox: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    padding: 10,
  },
  input: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    background: "#fff",
  },
  textarea: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    background: "#fff",
    resize: "vertical",
  },
  primaryBtn: {
    background: "#2563eb",
    color: "white",
    border: "1px solid #1e40af",
    borderRadius: 6,
    padding: "8px 12px",
    cursor: "pointer",
  },
  secondaryBtn: {
    background: "#f3f4f6",
    color: "#111827",
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    padding: "8px 12px",
    cursor: "pointer",
  },
  ghostBtn: {
    background: "transparent",
    color: "#111827",
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    padding: "8px 12px",
    cursor: "pointer",
  },
  errorBox: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#7f1d1d",
    padding: 10,
    borderRadius: 6,
    marginBottom: 12,
  },
};
